import { useBlocker } from '../../hooks/useBlocker';
import { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { FileText, Upload, X, Loader2, ArrowLeft } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useProcessing } from '../../context/ProcessingContext';
import { renderMarkdown } from '../../lib/markdoc';
import { supabase } from '../../lib/supabase';
import { Alert } from '../../components/Alert';
import { getFeatureCreditCostCache, setFeatureCreditCostCache } from '../../lib/cache';
import { useNavigate, Link } from 'react-router-dom';
import { FeaturePageFavoriteButton } from '../../components/FeaturePageFavoriteButton';
import clsx from 'clsx';

export function LoIAnalysis() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { blockNavigation } = useBlocker();

  const [file, setFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [result, setResult] = useState<string | null>(null);
  const [streamOutput, setStreamOutput] = useState<string>('');
  const [creditCost, setCreditCost] = useState<number>(5);
  const [featureId, setFeatureId] = useState<string | null>(null);
  const [isFavorited, setIsFavorited] = useState(false);

  const validateFilename = (filename: string): string | null => {
    const allowedCharacters = /^[a-zA-Z0-9_\-\.]+$/;
    const startsWithLetterOrNumber = /^[a-zA-Z0-9]/;
    const maxLength = 24;

    if (!startsWithLetterOrNumber.test(filename)) {
      return "Filename must start with a letter or number";
    }

    if (!allowedCharacters.test(filename)) {
      return "Invalid filename. Only a-z, A-Z, 0-9, _, -, and . are permitted.";
    }

    if (filename.length > maxLength) {
      return "Filename exceeds the maximum length of 24 characters.";
    }

    return null;
  };

  const {
    isProcessing,
    setIsProcessing,
    setProcessingMessage,
    error,
    setError,
    resetProcessingState
  } = useProcessing();

  useEffect(() => {
    if (!user) return;

    const fetchFeatureData = async () => {
      try {
        // Get feature ID and favorite status
        const { data: featureData, error: featureError } = await supabase
          .from('features')
          .select('id')
          .eq('slug', 'loi-analysis')
          .single();

        if (featureError) throw featureError;
        setFeatureId(featureData.id);

        // Check if favorited
        const { data: favoriteData, error: favoriteError } = await supabase
          .from('user_favorites')
          .select('feature_id')
          .eq('user_id', user.id)
          .eq('feature_id', featureData.id)
          .maybeSingle();

        if (favoriteError) throw favoriteError;
        setIsFavorited(!!favoriteData);
      } catch (error) {
        console.error('Error fetching feature data:', error);
      }
    };

    fetchFeatureData();
  }, [user]);

  useEffect(() => {
    if (isProcessing) {
      const unblock = navigate((to) => {
        return blockNavigation(to.pathname);
      });
      return unblock;
    }
  }, [isProcessing, navigate, blockNavigation]);

  useEffect(() => {
    const fetchCreditCost = async () => {
      const cachedCreditCost = getFeatureCreditCostCache('loi-analysis');
      if (cachedCreditCost !== null) {
        setCreditCost(cachedCreditCost);
        return;
      }

      const { data, error } = await supabase
        .from('features')
        .select('credit_cost')
        .eq('slug', 'loi-analysis')
        .single();

      if (!error && data) {
        setCreditCost(data.credit_cost);
        setFeatureCreditCostCache('loi-analysis', data.credit_cost);
      }
    };

    fetchCreditCost();
  }, []);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      const filenameError = validateFilename(file.name);
      if (filenameError) {
        setError(filenameError);
        setProcessingMessage(filenameError);
        setFile(null);
        setResult(null);
        setUploadProgress(0);
        return;
      }
      setFile(file);
      setError(null);
      setProcessingMessage('');
      setResult(null);
      setUploadProgress(0);
    }
  }, [setError, setProcessingMessage]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024,
    onDrop,
    onDropRejected: (fileRejections) => {
      const error = fileRejections[0]?.errors[0];
      if (error?.code === 'file-too-large') {
        setError('File is too large. Maximum size is 10MB.');
        setProcessingMessage('File is too large. Maximum size is 10MB.');
      } else if (error?.code === 'too-many-files') {
        setError('Please upload only one file at a time.');
        setProcessingMessage('too-many-files');
      } else {
        setError('Invalid file type. Please upload a PDF or DOCX file.');
        setProcessingMessage('Invalid file type. Please upload a PDF or DOCX file.');
      }
    },
    disabled: isSubmitting,
  });

  const clearFile = useCallback(() => {
    setFile(null);
    setError(null);
    setResult(null);
    setUploadProgress(0);
  }, [setError]);

  const handleSubmit = async () => {
    if (!user || !file) return;

    try {
      resetProcessingState();
      setIsProcessing(true);
      setProcessingMessage('Uploading file...');
      setUploadProgress(0);

      const { data: { session }, error: authError } = await supabase.auth.getSession();
      if (authError || !session) {
        throw new Error('Authentication error');
      }

      const formData = new FormData();
      formData.append('file', file);

      const uploadUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/file-upload`;
      const uploadResponse = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'X-Function-Slug': 'loi-analysis',
        },
        body: formData,
      });

      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json();
        throw new Error(errorData.error || 'File upload failed');
      }

      const uploadResult = await uploadResponse.json();
      setProcessingMessage('File uploaded successfully. Starting pre analysis...');

      const workflowUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/run-workflow-file`;
      const workflowResponse = await fetch(workflowUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
          'X-Function-Slug': 'loi-pre-analysis',
        },
        body: JSON.stringify({ fileId: uploadResult.id }),
      });

      if (!workflowResponse.ok) {
        throw new Error('Failed to start analysis');
      }

      const reader = workflowResponse.body?.getReader();
      if (!reader) {
        throw new Error('Failed to get response reader');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const event = JSON.parse(line.slice(6));

            switch (event.event) {
              case 'workflow_started':
                setProcessingMessage('Working on it... Larger files may take longer.');
                break;
              case 'workflow_finished':
                if (event.data?.status === 'succeeded') {
                  if (event.data.outputs.text === "YES") {
                    setProcessingMessage('Preparing LoI Analysis...');
                    const asyncWorkflowUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/async-run-workflow-file`;
                    const asyncWorkflowResponse = await fetch(asyncWorkflowUrl, {
                      method: 'POST',
                      headers: {
                        'Authorization': `Bearer ${session.access_token}`,
                        'Content-Type': 'application/json',
                        'X-Function-Slug': 'loi-analysis',
                      },
                      body: JSON.stringify({
                        fileId: uploadResult.id,
                      }),
                    });

                    if (!asyncWorkflowResponse.ok) {
                      throw new Error('Failed to start full analysis');
                    }

                    const asyncReader = asyncWorkflowResponse.body?.getReader();

                    setResult("Your LoI analysis is now in progress. Please check it in **Home -> My Reports** in 2-5 minutes. If you encounter any issues retrieving your report, please reach out to us at support@axoncre.com.");

                    if (!asyncReader) {
                      throw new Error('Failed to get async response reader');
                    }

                    const asyncDecoder = new TextDecoder();
                    let asyncBuffer = '';

                    while (true) {
                      const { done, value } = await asyncReader.read();
                      if (done) break;

                      const chunk = asyncDecoder.decode(value, { stream: true });
                      asyncBuffer += chunk;
                      setStreamOutput(asyncBuffer);
                    }
                  } else {
                    setResult(event.data.outputs.text);
                  }
                } else {
                  throw new Error('Analysis failed');
                }
                break;
              case 'error':
                throw new Error(event.error || 'Analysis failed');
            }
          }
        }
      }

      setProcessingMessage('Analysis completed successfully!');
    } catch (error) {
      console.error('Submission failed:', error);
      setError(error instanceof Error ? error.message : 'An error occurred');
      setProcessingMessage(error instanceof Error ? error.message : 'An error occurred');
    } finally {
      setIsProcessing(false);
      setUploadProgress(0);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <Link
        to="/dashboard"
        className="inline-flex items-center text-[--text-secondary] hover:text-[--text-primary] mb-8"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back
      </Link>

      <div className="mb-6 sm:mb-8">
        <div className="flex justify-between items-center">
          <h1 className="text-xl sm:text-2xl font-bold text-[--text-primary]">
            Letter of Intent Analysis
          </h1>
          {featureId && (
            <FeaturePageFavoriteButton
              featureId={featureId}
              initialIsFavorited={isFavorited}
            />
          )}
        </div>
        <div className="space-y-2">
          <p className="mt-2 text-sm sm:text-base text-[--text-secondary]">
            Gain strategic clarity in minutes. Upload your Letter of Intent to uncover critical questions and a structured summary that empowers better lease negotiations.
          </p>
          <div className="inline-flex items-center px-3 py-1 rounded-md bg-primary-50 dark:bg-primary-900/30">
            <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
              {creditCost} credits to run this feature
            </span>
          </div>
        </div>
      </div>

      <div className="bg-[--bg-primary] rounded-lg shadow p-4 sm:p-6">
        <p className="mb-4 font-semibold text-sm text-primary-700 dark:text-primary-300">
          We respect your privacy. Your uploaded documents are not stored, shared, or used for any secondary purposes. All files are processed securely and deleted after analysis.
        </p>
        {error && (
          <Alert type="error" className="mb-4">
            {error}
          </Alert>
        )}

        <div
          {...getRootProps()}
          className={clsx(
            "mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md min-h-[120px] cursor-pointer",
            isDragActive
              ? "border-primary-500 bg-primary-50 dark:bg-primary-900/20"
              : "border-[--border-secondary] hover:border-[--border-primary]"
          )}
        >
          <div className="space-y-1 text-center">
            <Upload className="mx-auto h-12 w-12 text-[--text-disabled]" />
            <div className="flex text-sm text-[--text-secondary]">
              <span className="relative font-medium text-[--link-default] hover:text-[--link-hover]">
                Upload a file
                <input {...getInputProps()} className="sr-only" />
              </span>
              <p className="pl-1">or drag and drop</p>
            </div>
            <p className="text-xs text-[--text-secondary]">
              PDF or DOCX up to 10MB
            </p>
          </div>
        </div>

        {file && (
          <div className="mt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center text-sm text-[--text-secondary]">
                <FileText className="mr-2 h-4 w-4" />
                {file.name}
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  clearFile();
                }}
                className="p-1 rounded-full hover:bg-[--state-hover]"
              >
                <X className="h-4 w-4 text-[--text-secondary]" />
              </button>
            </div>

            {uploadProgress > 0 && uploadProgress < 100 && (
              <div className="mt-2">
                <div className="bg-[--bg-tertiary] rounded-full h-2">
                  <div
                    className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <p className="mt-1 text-xs text-[--text-secondary]">
                  Uploading: {uploadProgress}%
                </p>
              </div>
            )}
          </div>
        )}

        <div className="mt-6">
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !file || !user || !user.credits || user.credits < creditCost}
            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
          >
            {isSubmitting ? (
              <span className="flex items-center">
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Processing...
              </span>
            ) : (
              user && user.credits < creditCost ? "You're out of credits for this feature." : 'Submit'
            )}
          </button>
        </div>
      </div>

      {result && (
        <div className="mt-8 bg-[--bg-primary] rounded-lg shadow overflow-hidden">
          <div className="p-6">
            <h2 className="text-lg font-medium text-[--text-primary] mb-4">
              Please Note
            </h2>
            <div className="prose dark:prose-invert max-w-none">
              {renderMarkdown(result)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}